import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import {
  Container, Typography, Box, Card, CardContent, Grid, Button, Chip,
  Avatar, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, CircularProgress, Alert,
  Stepper, Step, StepLabel,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import SailingIcon from '@mui/icons-material/Sailing';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import StripeConnectSection from '../../components/StripeConnectSection';

/* ── Design tokens ──────────────────────────────────────────────── */
const T = {
  bg: '#E4F0F6',
  surface: '#fff',
  purple: '#552583',
  purpleLight: 'rgba(85,37,131,0.08)',
  gold: '#FDB927',
  goldDark: '#e5a620',
  green: '#22c55e',
  red: '#ef4444',
  orange: '#f59e0b',
  text: '#1a1a1a',
  soft: '#666',
  muted: '#999',
  line: 'rgba(0,0,0,0.08)',
  radius: '10px',
};

const VEHICLE_TYPES = [
  { value: 'auto',   label: 'Voiture',  icon: <DirectionsCarIcon /> },
  { value: 'moto',   label: 'Moto / Scooter', icon: <TwoWheelerIcon /> },
  { value: 'bateau', label: 'Bateau',   icon: <SailingIcon /> },
];

const CATEGORIES_BY_TYPE = {
  auto:   ['Berline', 'SUV', 'Citadine', 'Coupé', 'Cabriolet', 'Utilitaire', 'Économique', 'Luxe'],
  moto:   ['Scooter', 'Quad', 'Cyclomoteur', 'Sportive'],
  bateau: ['Barque', 'Bateau à moteur', 'Jetski', 'Kayak'],
};

const PHOTO_FIELDS = [
  { key: 'photoArriere',    label: 'Arrière' },
  { key: 'photoDroit',      label: 'Côté droit' },
  { key: 'photoGauche',     label: 'Côté gauche' },
  { key: 'photoInterieur',  label: 'Intérieur' },
  { key: 'photoTableauBord',label: 'Tableau de bord' },
  { key: 'photoParechoc',   label: 'Pare-choc / Avant' },
];

const DOC_FIELDS = [
  { key: 'identity',         label: "Pièce d'identité",    required: true },
  { key: 'carteGrise',       label: 'Carte grise',         required: true },
  { key: 'insuranceDoc',     label: 'Assurance',           required: true },
  { key: 'controleTechnique',label: 'Contrôle technique',  required: false },
];

const STEPS = ['Type & Infos', 'Photos', 'Documents'];

const CarRentalPartner = () => {
  const [tabValue, setTabValue] = useState(0);
  const [cars, setCars] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [brands, setBrands] = useState([]);
  const [carDialog, setCarDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  const [carForm, setCarForm] = useState({
    vehicleType: 'auto', brand: '', model: '', year: '', category: '',
    transmission: 'Automatique', fuel: 'Essence', seats: 5,
    luggage: 3, pricePerDay: '', insurance: 15, deposit: 200, description: '',
  });

  const [photos, setPhotos] = useState({});
  const [photoPreviews, setPhotoPreviews] = useState({});
  const [docs, setDocs] = useState({});
  const [docNames, setDocNames] = useState({});
  const fileRefs = useRef({});

  useEffect(() => {
    loadCars();
    loadBookings();
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      const response = await api.get('/car-rentals/brands');
      setBrands(response.data.brands || []);
    } catch { setBrands([]); }
  };

  const loadCars = async () => {
    try {
      const response = await api.get('/car-rentals/my-vehicles');
      setCars(response.data.vehicles || []);
    } catch { setCars([]); }
  };

  const loadBookings = async () => {
    try {
      const response = await api.get('/car-rentals/partner/bookings');
      setBookings(response.data.bookings || []);
    } catch { setBookings([]); }
  };

  const handleAddCar = () => {
    setCarForm({
      vehicleType: 'auto', brand: '', model: '', year: '', category: '',
      transmission: 'Automatique', fuel: 'Essence', seats: 5,
      luggage: 3, pricePerDay: '', insurance: 15, deposit: 200, description: '',
    });
    setPhotos({}); setPhotoPreviews({}); setDocs({}); setDocNames({});
    setActiveStep(0); setSubmitError(''); setSubmitSuccess('');
    setCarDialog(true);
  };

  const handlePhotoChange = (key, file) => {
    if (!file) return;
    setPhotos(p => ({ ...p, [key]: file }));
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreviews(p => ({ ...p, [key]: e.target.result }));
    reader.readAsDataURL(file);
  };

  const handleDocChange = (key, file) => {
    if (!file) return;
    setDocs(d => ({ ...d, [key]: file }));
    setDocNames(n => ({ ...n, [key]: file.name }));
  };

  const canProceedStep = () => {
    if (activeStep === 0) {
      return carForm.brand && carForm.model && carForm.year && carForm.pricePerDay;
    }
    if (activeStep === 1) {
      return PHOTO_FIELDS.every(f => photos[f.key]);
    }
    if (activeStep === 2) {
      const currentYear = new Date().getFullYear();
      const vehicleYear = parseInt(carForm.year);
      const needsControle = (currentYear - vehicleYear) >= 4;
      const hasRequired = docs.identity && docs.carteGrise && docs.insuranceDoc;
      if (needsControle) return hasRequired && docs.controleTechnique;
      return hasRequired;
    }
    return false;
  };

  const handleSubmitVehicle = async () => {
    setSubmitting(true); setSubmitError('');
    try {
      const formData = new FormData();
      // Info fields
      formData.append('vehicleType', carForm.vehicleType);
      formData.append('brand', carForm.brand);
      formData.append('model', carForm.model);
      formData.append('year', carForm.year);
      formData.append('category', carForm.category);
      formData.append('transmission', carForm.transmission);
      formData.append('fuel', carForm.fuel);
      formData.append('seats', carForm.seats);
      formData.append('luggage', carForm.luggage);
      formData.append('pricePerDay', carForm.pricePerDay);
      formData.append('insurance', carForm.insurance);
      formData.append('deposit', carForm.deposit);
      formData.append('description', carForm.description);
      // Photos
      PHOTO_FIELDS.forEach(f => {
        if (photos[f.key]) formData.append(f.key, photos[f.key]);
      });
      // Documents
      DOC_FIELDS.forEach(f => {
        if (docs[f.key]) formData.append(f.key, docs[f.key]);
      });

      await api.post('/car-rentals/my-vehicles', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitSuccess('Véhicule soumis avec succès ! Votre demande est en cours de vérification.');
      loadCars();
      setTimeout(() => { setCarDialog(false); setSubmitSuccess(''); }, 2500);
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusChip = (status) => {
    const map = {
      pending:  { label: 'En attente',  bg: T.orange, color: '#fff' },
      verified: { label: 'Approuvé',    bg: T.green,  color: '#fff' },
      rejected: { label: 'Refusé',      bg: T.red,    color: '#fff' },
    };
    const s = map[status] || map.pending;
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.3,
        borderRadius: '6px', bgcolor: s.bg, color: s.color, fontSize: '0.7rem', fontWeight: 700 }}>
        {status === 'pending' && <WarningIcon sx={{ fontSize: 12 }} />}
        {status === 'verified' && <CheckCircleIcon sx={{ fontSize: 12 }} />}
        {status === 'rejected' && <CancelIcon sx={{ fontSize: 12 }} />}
        {s.label}
      </Box>
    );
  };

  const getBookingStatusColor = (status) => {
    const colors = { pending: 'warning', confirmed: 'info', active: 'success', completed: 'default', cancelled: 'error' };
    return colors[status] || 'default';
  };

  const getBookingStatusLabel = (status) => {
    const labels = { pending: 'En attente', confirmed: 'Confirmée', active: 'En cours', completed: 'Terminée', cancelled: 'Annulée' };
    return labels[status] || status;
  };

  const stats = {
    totalCars: cars.length,
    availableCars: cars.filter(c => c.available).length,
    pendingCars: cars.filter(c => c.verificationStatus === 'pending').length,
    activeBookings: bookings.filter(b => b.status === 'active').length,
  };

  const needsControle = () => {
    const currentYear = new Date().getFullYear();
    const vehicleYear = parseInt(carForm.year);
    return carForm.year && (currentYear - vehicleYear) >= 4;
  };

  /* ── Upload box component ─────────────────────────────────────── */
  const UploadBox = ({ fieldKey, label, preview, isPhoto, docName }) => (
    <Box
      onClick={() => fileRefs.current[fieldKey]?.click()}
      sx={{
        border: `2px dashed ${preview || docName ? T.purple : T.line}`,
        borderRadius: T.radius, p: 1.5, cursor: 'pointer', textAlign: 'center',
        bgcolor: preview || docName ? T.purpleLight : 'rgba(0,0,0,0.02)',
        transition: 'all 0.2s',
        '&:hover': { borderColor: T.purple, bgcolor: T.purpleLight },
        position: 'relative', overflow: 'hidden',
        height: isPhoto ? 120 : 'auto', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <input
        ref={el => fileRefs.current[fieldKey] = el}
        type="file"
        accept={isPhoto ? 'image/*' : 'image/*,.pdf'}
        style={{ display: 'none' }}
        onChange={(e) => isPhoto ? handlePhotoChange(fieldKey, e.target.files[0]) : handleDocChange(fieldKey, e.target.files[0])}
      />
      {isPhoto && preview ? (
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <img src={preview} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
          <Box sx={{ position: 'absolute', bottom: 4, left: 4, right: 4, bgcolor: 'rgba(85,37,131,0.85)',
            borderRadius: '4px', py: 0.3, px: 0.8 }}>
            <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 600 }}>{label}</Typography>
          </Box>
        </Box>
      ) : docName ? (
        <>
          <CheckCircleIcon sx={{ fontSize: 20, color: T.green, mb: 0.3 }} />
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: T.text }}>{label}</Typography>
          <Typography sx={{ fontSize: '0.58rem', color: T.muted, maxWidth: '100%', overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{docName}</Typography>
        </>
      ) : (
        <>
          {isPhoto ? <PhotoCameraIcon sx={{ fontSize: 22, color: T.muted, mb: 0.3 }} />
                    : <DescriptionIcon sx={{ fontSize: 22, color: T.muted, mb: 0.3 }} />}
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: T.soft }}>{label}</Typography>
          <Typography sx={{ fontSize: '0.55rem', color: T.muted }}>Cliquez pour ajouter</Typography>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: T.bg, minHeight: '100vh', pb: 6 }}>
      {/* Header */}
      <Box sx={{ bgcolor: T.purple, color: '#fff', py: 4 }}>
        <Container maxWidth="xl">
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.03em' }}>
            Gestion Location
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', opacity: 0.8, mt: 0.5 }}>
            Gérez votre flotte de véhicules, motos et bateaux
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: -3 }}>
        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Véhicules total', value: stats.totalCars, icon: <DirectionsCarIcon />, color: T.purple },
            { label: 'Disponibles', value: stats.availableCars, icon: <DirectionsCarIcon />, color: T.green },
            { label: 'En attente', value: stats.pendingCars, icon: <WarningIcon />, color: T.orange },
            { label: 'Locations actives', value: stats.activeBookings, icon: <TrendingUpIcon />, color: T.gold },
          ].map((s, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Card sx={{ borderRadius: T.radius, border: `1px solid ${T.line}`, boxShadow: 'none' }}>
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ bgcolor: `${s.color}15`, color: s.color, width: 44, height: 44 }}>
                      {s.icon}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: T.text, lineHeight: 1 }}>
                        {s.value}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: T.muted, fontWeight: 600 }}>
                        {s.label}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 3, borderRadius: T.radius, border: `1px solid ${T.line}`, boxShadow: 'none', overflow: 'hidden' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}
            sx={{
              '& .MuiTab-root': { fontWeight: 700, fontSize: '0.78rem', textTransform: 'none',
                color: T.soft, '&.Mui-selected': { color: T.purple } },
              '& .MuiTabs-indicator': { bgcolor: T.purple, height: 2.5 },
            }}>
            <Tab label="Réservations" />
            <Tab label="Ma Flotte" />
            <Tab label="Statistiques" />
            <Tab label="Paiements" />
          </Tabs>
        </Paper>

        {/* ── Tab 0: Bookings ──────────────────────────────────────── */}
        {tabValue === 0 && (
          <Card sx={{ borderRadius: T.radius, border: `1px solid ${T.line}`, boxShadow: 'none' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(85,37,131,0.04)' }}>
                    {['Véhicule','Client','Période','Prix','Commission','Net','Statut'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.7rem', color: T.soft,
                        borderBottom: `1px solid ${T.line}`, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: T.muted }}>
                        Aucune réservation
                      </TableCell>
                    </TableRow>
                  ) : bookings.map((b) => (
                    <TableRow key={b.id} sx={{ '&:hover': { bgcolor: 'rgba(85,37,131,0.02)' } }}>
                      <TableCell sx={{ borderBottom: `1px solid ${T.line}` }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{b.carName}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${T.line}` }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{b.customerName}</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: T.muted }}>{b.customerPhone}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${T.line}` }}>
                        <Typography sx={{ fontSize: '0.75rem' }}>{b.pickupDate}</Typography>
                        <Typography sx={{ fontSize: '0.75rem' }}>{b.returnDate}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${T.line}` }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{b.totalPrice} EUR</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${T.line}` }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: T.red }}>-{b.commission} EUR</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${T.line}` }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: T.green }}>{b.netAmount} EUR</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${T.line}` }}>
                        <Chip label={getBookingStatusLabel(b.status)} color={getBookingStatusColor(b.status)} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {/* ── Tab 1: Fleet ─────────────────────────────────────────── */}
        {tabValue === 1 && (
          <>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCar}
                sx={{ bgcolor: T.purple, color: '#fff', fontWeight: 700, borderRadius: T.radius,
                  textTransform: 'none', px: 3, '&:hover': { bgcolor: '#3d1a62' } }}>
                Ajouter un vehicule
              </Button>
            </Box>

            {cars.length === 0 ? (
              <Card sx={{ borderRadius: T.radius, border: `1px solid ${T.line}`, boxShadow: 'none', p: 6, textAlign: 'center' }}>
                <DirectionsCarIcon sx={{ fontSize: 48, color: T.muted, mb: 1 }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: T.soft }}>Aucun vehicule</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: T.muted, mb: 2 }}>
                  Ajoutez votre premier vehicule pour commencer
                </Typography>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddCar}
                  sx={{ borderColor: T.purple, color: T.purple, fontWeight: 600, borderRadius: T.radius, textTransform: 'none' }}>
                  Ajouter un vehicule
                </Button>
              </Card>
            ) : (
              <Grid container spacing={2}>
                {cars.map((car) => (
                  <Grid item xs={12} sm={6} md={4} key={car.id}>
                    <Card sx={{ borderRadius: T.radius, border: `1px solid ${T.line}`, boxShadow: 'none',
                      overflow: 'hidden', transition: 'all 0.2s', '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' } }}>
                      {/* Car image */}
                      <Box sx={{ height: 140, bgcolor: 'rgba(85,37,131,0.06)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                        {car.image ? (
                          <img src={car.image} alt={`${car.brand} ${car.model}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          car.vehicleType === 'moto' ? <TwoWheelerIcon sx={{ fontSize: 48, color: T.muted }} /> :
                          car.vehicleType === 'bateau' ? <SailingIcon sx={{ fontSize: 48, color: T.muted }} /> :
                          <DirectionsCarIcon sx={{ fontSize: 48, color: T.muted }} />
                        )}
                        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                          {getStatusChip(car.verificationStatus)}
                        </Box>
                      </Box>
                      <CardContent sx={{ pt: 1.5, pb: '12px !important' }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: T.text }}>
                          {car.brand} {car.model}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: T.muted, mb: 1 }}>
                          {car.year} - {car.category} - {car.transmission}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: T.purple }}>
                            {car.pricePerDay} EUR<Typography component="span" sx={{ fontSize: '0.7rem', color: T.muted }}>/jour</Typography>
                          </Typography>
                          <Chip
                            label={car.available ? 'Disponible' : 'Indisponible'}
                            size="small"
                            sx={{
                              bgcolor: car.available ? `${T.green}15` : `${T.red}15`,
                              color: car.available ? T.green : T.red,
                              fontWeight: 600, fontSize: '0.65rem',
                            }}
                          />
                        </Box>
                        {car.verificationStatus === 'rejected' && car.rejectionReason && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: `${T.red}08`, borderRadius: '6px', borderLeft: `3px solid ${T.red}` }}>
                            <Typography sx={{ fontSize: '0.68rem', color: T.red, fontWeight: 600 }}>
                              Motif du refus : {car.rejectionReason}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {/* ── Tab 2: Statistics ────────────────────────────────────── */}
        {tabValue === 2 && (
          <Card sx={{ borderRadius: T.radius, border: `1px solid ${T.line}`, boxShadow: 'none' }}>
            <CardContent>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: T.text, mb: 2 }}>
                Statistiques Financieres
              </Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'CA Total', value: '0 EUR', bg: T.purple },
                  { label: 'Commission Usafiri', value: '0 EUR', bg: T.red },
                  { label: 'Revenu Net', value: '0 EUR', bg: T.green },
                ].map((s, i) => (
                  <Grid item xs={12} sm={4} key={i}>
                    <Box sx={{ bgcolor: s.bg, p: 3, borderRadius: T.radius, color: '#fff' }}>
                      <Typography sx={{ fontSize: '0.75rem', opacity: 0.85 }}>{s.label}</Typography>
                      <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, mt: 0.5 }}>{s.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* ── Tab 3: Paiements ────────────────────────────────────── */}
        {tabValue === 3 && (
          <StripeConnectSection commissionRate={15} commissionLabel="sur le montant des locations" />
        )}
      </Container>

      {/* ════════════════════════════════════════════════════════════
          DIALOG: Ajouter un vehicule (stepper)
         ════════════════════════════════════════════════════════════ */}
      <Dialog open={carDialog} onClose={() => !submitting && setCarDialog(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden' } }}>
        {/* Title bar */}
        <Box sx={{ bgcolor: T.purple, color: '#fff', px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>Ajouter un vehicule</Typography>
          <IconButton onClick={() => !submitting && setCarDialog(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Stepper */}
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Stepper activeStep={activeStep} alternativeLabel
            sx={{
              '& .MuiStepLabel-label': { fontSize: '0.72rem', fontWeight: 600 },
              '& .MuiStepIcon-root.Mui-active': { color: T.purple },
              '& .MuiStepIcon-root.Mui-completed': { color: T.green },
            }}>
            {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
          </Stepper>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2 }}>
          {submitError && <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setSubmitError('')}>{submitError}</Alert>}
          {submitSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: '8px' }}>{submitSuccess}</Alert>}

          {/* ── Step 0: Type & Info ──────────────────────────────── */}
          {activeStep === 0 && (
            <Box>
              {/* Vehicle type selector */}
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: T.text, mb: 1.5 }}>
                Type de vehicule
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                {VEHICLE_TYPES.map(vt => (
                  <Box key={vt.value}
                    onClick={() => setCarForm({ ...carForm, vehicleType: vt.value, category: '' })}
                    sx={{
                      flex: 1, py: 2, px: 1.5, borderRadius: T.radius, cursor: 'pointer',
                      border: `2px solid ${carForm.vehicleType === vt.value ? T.purple : T.line}`,
                      bgcolor: carForm.vehicleType === vt.value ? T.purpleLight : 'transparent',
                      textAlign: 'center', transition: 'all 0.2s',
                      '&:hover': { borderColor: T.purple, bgcolor: T.purpleLight },
                    }}>
                    <Box sx={{ color: carForm.vehicleType === vt.value ? T.purple : T.muted, mb: 0.5,
                      '& svg': { fontSize: 28 } }}>
                      {vt.icon}
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700,
                      color: carForm.vehicleType === vt.value ? T.purple : T.soft }}>
                      {vt.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Marque</InputLabel>
                    <Select value={carForm.brand} label="Marque"
                      onChange={(e) => setCarForm({ ...carForm, brand: e.target.value })}>
                      {brands.map((b) => <MenuItem key={b.id} value={b.name}>{b.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Modele" value={carForm.model}
                    onChange={(e) => setCarForm({ ...carForm, model: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Annee" type="number" value={carForm.year}
                    onChange={(e) => setCarForm({ ...carForm, year: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Categorie</InputLabel>
                    <Select value={carForm.category} label="Categorie"
                      onChange={(e) => setCarForm({ ...carForm, category: e.target.value })}>
                      {(CATEGORIES_BY_TYPE[carForm.vehicleType] || []).map(c => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Transmission</InputLabel>
                    <Select value={carForm.transmission} label="Transmission"
                      onChange={(e) => setCarForm({ ...carForm, transmission: e.target.value })}>
                      <MenuItem value="Automatique">Automatique</MenuItem>
                      <MenuItem value="Manuelle">Manuelle</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Carburant</InputLabel>
                    <Select value={carForm.fuel} label="Carburant"
                      onChange={(e) => setCarForm({ ...carForm, fuel: e.target.value })}>
                      <MenuItem value="Essence">Essence</MenuItem>
                      <MenuItem value="Diesel">Diesel</MenuItem>
                      <MenuItem value="Electrique">Electrique</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Places" type="number" value={carForm.seats}
                    onChange={(e) => setCarForm({ ...carForm, seats: parseInt(e.target.value) || 5 })} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Prix/jour (EUR)" type="number" value={carForm.pricePerDay}
                    onChange={(e) => setCarForm({ ...carForm, pricePerDay: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Assurance/jour (EUR)" type="number" value={carForm.insurance}
                    onChange={(e) => setCarForm({ ...carForm, insurance: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Caution (EUR)" type="number" value={carForm.deposit}
                    onChange={(e) => setCarForm({ ...carForm, deposit: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Description (optionnel)" multiline rows={2}
                    value={carForm.description}
                    onChange={(e) => setCarForm({ ...carForm, description: e.target.value })} />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* ── Step 1: Photos ─────────────────────────────────────── */}
          {activeStep === 1 && (
            <Box>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: T.text, mb: 0.5 }}>
                Photos du vehicule (6 obligatoires)
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: T.muted, mb: 2 }}>
                Prenez des photos claires sous bonne lumiere. Chaque angle est obligatoire.
              </Typography>
              <Grid container spacing={1.5}>
                {PHOTO_FIELDS.map(f => (
                  <Grid item xs={6} sm={4} key={f.key}>
                    <UploadBox fieldKey={f.key} label={f.label} preview={photoPreviews[f.key]} isPhoto />
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                  bgcolor: PHOTO_FIELDS.every(f => photos[f.key]) ? T.green : T.orange }} />
                <Typography sx={{ fontSize: '0.72rem', color: T.soft }}>
                  {Object.keys(photos).length}/{PHOTO_FIELDS.length} photos ajoutees
                </Typography>
              </Box>
            </Box>
          )}

          {/* ── Step 2: Documents ──────────────────────────────────── */}
          {activeStep === 2 && (
            <Box>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: T.text, mb: 0.5 }}>
                Documents du vehicule
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: T.muted, mb: 2 }}>
                Formats acceptes : image ou PDF. Les documents seront verifies par l'equipe Usafiri.
              </Typography>
              <Grid container spacing={1.5}>
                {DOC_FIELDS.map(f => {
                  const isRequired = f.required || (f.key === 'controleTechnique' && needsControle());
                  return (
                    <Grid item xs={12} sm={6} key={f.key}>
                      <UploadBox fieldKey={f.key} label={`${f.label}${isRequired ? ' *' : ' (optionnel)'}`}
                        docName={docNames[f.key]} isPhoto={false} />
                    </Grid>
                  );
                })}
              </Grid>
              {needsControle() && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: `${T.orange}12`, borderRadius: '8px', borderLeft: `3px solid ${T.orange}` }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: T.orange }}>
                    Le controle technique est obligatoire pour les vehicules de plus de 4 ans
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${T.line}`, gap: 1 }}>
          {activeStep > 0 && (
            <Button onClick={() => setActiveStep(s => s - 1)} disabled={submitting}
              sx={{ color: T.soft, textTransform: 'none', fontWeight: 600 }}>
              Retour
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setCarDialog(false)} disabled={submitting}
            sx={{ color: T.muted, textTransform: 'none' }}>
            Annuler
          </Button>
          {activeStep < 2 ? (
            <Button variant="contained" onClick={() => setActiveStep(s => s + 1)}
              disabled={!canProceedStep()}
              sx={{ bgcolor: T.purple, color: '#fff', textTransform: 'none', fontWeight: 700,
                borderRadius: '8px', px: 3, '&:hover': { bgcolor: '#3d1a62' },
                '&.Mui-disabled': { bgcolor: 'rgba(85,37,131,0.3)', color: 'rgba(255,255,255,0.5)' } }}>
              Suivant
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSubmitVehicle}
              disabled={!canProceedStep() || submitting}
              startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CloudUploadIcon />}
              sx={{ bgcolor: T.green, color: '#fff', textTransform: 'none', fontWeight: 700,
                borderRadius: '8px', px: 3, '&:hover': { bgcolor: '#1aa34a' },
                '&.Mui-disabled': { bgcolor: 'rgba(34,197,94,0.3)', color: 'rgba(255,255,255,0.5)' } }}>
              {submitting ? 'Envoi en cours...' : 'Soumettre la demande'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CarRentalPartner;
